import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectAccessLevel } from '../slices/subscriptions/subscriptionsSlice';
import { examsSelectors } from '../slices/exams/examsSlice';
import { SubscriptionAccess } from '../types/Subscription';

/**
 * Numeric hierarchy for comparing access levels.
 */
const ACCESS_HIERARCHY = {
  [SubscriptionAccess.FREE]: 0,
  [SubscriptionAccess.PRE_CLINICAL]: 1,
  [SubscriptionAccess.STEP_SHELF]: 2,
  [SubscriptionAccess.TRIAL]: 2,
};

/**
 * Returns a human-friendly plan name for a given access level.
 *
 * @param access - The subscription access level.
 * @returns The display name for the plan.
 */
export function getPlanName(access: SubscriptionAccess): string {
  if (access === SubscriptionAccess.FREE) return 'Free';
  if (access === SubscriptionAccess.PRE_CLINICAL) return 'Preclinical';
  if (access === SubscriptionAccess.STEP_SHELF) return 'Pro';
  return 'Trial';
}

/**
 * Hook that exposes access gating, plan name, limits, and current access level.
 *
 * @returns Helper utilities derived from the current user's subscription access.
 */
export function useAccess() {
  const primaryExamAccessLevel = useSelector(
    examsSelectors.selectPrimaryExamAccessLevel
  );

  const accessLevel = useSelector(selectAccessLevel);

  const isGated = useCallback(() => {
    return accessLevel === SubscriptionAccess.FREE
      ? true
      : ACCESS_HIERARCHY[accessLevel] <
          ACCESS_HIERARCHY[primaryExamAccessLevel];
  }, [accessLevel, primaryExamAccessLevel]);

  const planName = useCallback(() => getPlanName(accessLevel), [accessLevel]);

  const limits = useMemo(
    () => ({
      qbank: 5,
      videos: 1,
      chatResponses: 15,
    }),
    []
  );

  return {
    isGated,
    limits,
    accessLevel,
    planName,
  };
}
